import { useEffect, useRef, useState } from 'react';
import type {
  ChecklistImportProductResult,
  ChecklistImportResultManifest,
  ChecklistImportVerificationReportResponse,
  ChecklistImportVerificationResponse,
} from '@tcvs/shared';
import { useAuth } from '../auth/AuthContext';
import { useChecklistImportResultsApi } from './checklist-import-results-api';
import {
  appendImportResultPage,
  downloadImportResults,
  isImportVerificationResponse,
  importResultsDownloadFits,
  readChecklistResultBatch,
  verifyImportResultReport,
} from './checklist-import-results-report';

type Action = 'start' | 'advance' | 'status' | 'report';
const unavailable = (count: number | null) => (count === null ? 'Unknown' : String(count));
export function ChecklistImportResults(): JSX.Element {
  const api = useChecklistImportResultsApi();
  const auth = useAuth();
  const identity = `${auth.claims?.sub ?? ''}:${auth.getAccessToken() ?? ''}`;
  const identityRef = useRef(identity);
  identityRef.current = identity;
  const generation = useRef(0);
  const running = useRef(false);
  const pending = useRef(new Map<string, string>());
  const cursors = useRef(new Set<string>());
  const [batchId, setBatchId] = useState(readChecklistResultBatch);
  const [manifest, setManifest] = useState<ChecklistImportResultManifest | null>(null);
  const [sessions, setSessions] = useState<Record<string, ChecklistImportVerificationResponse>>({});
  const [reports, setReports] = useState<Record<string, ChecklistImportVerificationReportResponse>>(
    {},
  );
  const [busy, setBusy] = useState(false);
  const [paused, setPaused] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const clear = () => {
    generation.current += 1;
    running.current = false;
    pending.current.clear();
    cursors.current.clear();
    setManifest(null);
    setSessions({});
    setReports({});
    setError(null);
    setBusy(false);
    setPaused(false);
  };
  useEffect(() => {
    clear();
    return () => {
      generation.current += 1;
      running.current = false;
    };
    // Auth identity is the boundary; API hook object identity need not be stable.
  }, [identity]);
  const begin = () => {
    if (running.current) return null;
    running.current = true;
    setBusy(true);
    setError(null);
    setPaused(false);
    const epoch = generation.current,
      owner = identity;
    return () =>
      epoch === generation.current &&
      identityRef.current === owner &&
      `${auth.claims?.sub ?? ''}:${auth.getAccessToken() ?? ''}` === owner;
  };
  const failure = (status?: number) =>
    status === 429
      ? 'The operator allowance is exhausted. Resume explicitly after it resets.'
      : status === 409
        ? 'Verification state changed. Check status before continuing.'
        : 'Results could not be read. Retry explicitly; no background work was started.';
  const load = async (next: boolean) => {
    const current = begin();
    if (!current) return;
    try {
      const cursor = next ? manifest?.membership.nextCursor : undefined;
      const result = await api.list(batchId.trim(), cursor);
      if (!current()) return;
      if (!result.ok) {
        setError(failure(result.status));
        return;
      }
      if (result.data.batchId !== batchId.trim()) throw new Error();
      if (
        result.data.membership?.nextCursor &&
        (result.data.membership.nextCursor === cursor ||
          cursors.current.has(result.data.membership.nextCursor))
      )
        throw new Error();
      const collection = appendImportResultPage(next ? manifest : null, result.data);
      if (!next) {
        cursors.current.clear();
        pending.current.clear();
        setSessions({});
        setReports({});
      }
      if (cursor) cursors.current.add(cursor);
      setManifest(collection);
    } catch {
      if (current())
        setError('Invalid or inconsistent results. Reload the batch before continuing.');
    } finally {
      if (current()) {
        running.current = false;
        setBusy(false);
      }
    }
  };
  const act = async (product: ChecklistImportProductResult, action: Action) => {
    if (!manifest) return;
    const current = begin();
    if (!current) return;
    const target = product.sourceTargetId;
    const summary = sessions[target]?.verification ?? product.verification;
    const requestKey = `${target}:${action}:${summary.sessionId ?? ''}:${summary.version}`;
    try {
      if (action === 'report') {
        if (!summary.reportId) throw new Error();
        const result = await api.report(manifest.batchId, target, summary.reportId);
        if (!current()) return;
        if (!result.ok) {
          setError(failure(result.status));
          return;
        }
        const valid = await verifyImportResultReport(result.data);
        if (!current()) return;
        const report = result.data.report;
        if (
          !valid ||
          report.batchId !== manifest.batchId ||
          report.batchFingerprint !== manifest.batchFingerprint ||
          report.sourceTargetId !== target ||
          report.jobId !== product.jobId ||
          report.fingerprint !== product.fingerprint ||
          report.reportId !== summary.reportId ||
          report.digest !== summary.digest
        )
          throw new Error();
        setReports((previous) => ({ ...previous, [target]: result.data }));
        return;
      }
      let requestId = pending.current.get(requestKey);
      if (!requestId) {
        requestId = crypto.randomUUID();
        pending.current.set(requestKey, requestId);
      }
      const result =
        action === 'start'
          ? await api.start(manifest.batchId, target, requestId)
          : action === 'advance' && summary.sessionId
            ? await api.advance(
                manifest.batchId,
                target,
                summary.sessionId,
                summary.version,
                requestId,
              )
            : summary.sessionId
              ? await api.status(manifest.batchId, target, summary.sessionId)
              : null;
      if (!current()) return;
      if (!result) throw new Error();
      if (!result.ok) {
        setError(failure(result.status));
        return;
      }
      if (
        !isImportVerificationResponse(result.data, product) ||
        result.data.batchId !== manifest.batchId ||
        result.data.sourceTargetId !== target ||
        (action !== 'start' && result.data.verification.sessionId !== summary.sessionId) ||
        result.data.verification.version < (action === 'start' ? 0 : summary.version)
      )
        throw new Error();
      pending.current.delete(requestKey);
      setSessions((previous) => ({ ...previous, [target]: result.data }));
      setReports((previous) => {
        const copy = { ...previous };
        delete copy[target];
        return copy;
      });
    } catch {
      if (current())
        setError(
          'Invalid verification evidence. No report was accepted. Check status or reload the batch.',
        );
    } finally {
      if (current()) {
        running.current = false;
        setBusy(false);
      }
    }
  };
  const save = (value: unknown, filename: string) => {
    try {
      downloadImportResults(value, filename);
    } catch {
      setError(
        'The retained results exceed the 8 MiB download limit. Download individual reports.',
      );
    }
  };
  const retained = manifest
    ? {
        ...manifest,
        products: manifest.products.map((product) => ({
          ...product,
          verification: sessions[product.sourceTargetId]?.verification ?? product.verification,
          freshness:
            reports[product.sourceTargetId]?.freshness ??
            sessions[product.sourceTargetId]?.freshness ??
            product.freshness,
        })),
      }
    : null;
  const download = retained
    ? {
        manifest: retained,
        verificationReports: Object.values(reports),
        verificationReportsComplete: retained.products.every((product) =>
          Boolean(reports[product.sourceTargetId]),
        ),
        applyAllowed: false,
      }
    : null;
  const downloadFits = download !== null && importResultsDownloadFits(download);
  return (
    <section aria-label="Import results">
      <h3>Import results and catalog verification</h3>
      <p>
        Import completion and catalog verification are separate. Historical input evidence may be
        UNKNOWN. Saved-card references remain UNKNOWN; these reports do not authorize changes.
      </p>
      <p>
        Each action reads one page or advances one target by a bounded step. There is no background
        polling. Pause stops local follow-up work; an in-flight server step may finish. Reload
        retains the last completed batch ID; load it again to recover server verification status.
      </p>
      <label>
        Batch ID{' '}
        <input
          aria-label="Batch ID"
          value={batchId}
          onChange={(event) => {
            clear();
            setBatchId(event.target.value);
          }}
        />
      </label>
      <button type="button" disabled={busy || !batchId.trim()} onClick={() => void load(false)}>
        Load batch results
      </button>
      <button
        type="button"
        onClick={() => {
          generation.current += 1;
          running.current = false;
          setBusy(false);
          setPaused(true);
        }}
      >
        Pause verification locally
      </button>
      <button type="button" onClick={clear}>
        Reset results
      </button>
      {paused && (
        <p>
          Paused locally. Use Check status to recover an in-flight step, or resume an explicit
          action.
        </p>
      )}
      {error && <p role="alert">{error}</p>}
      {retained && (
        <>
          <p>
            Batch membership: {retained.membership.coverage}. Loaded {retained.products.length} of{' '}
            {unavailable(retained.membership.total)} products. Collection:{' '}
            {retained.complete ? 'COMPLETE' : 'INCOMPLETE'}.
          </p>
          <p>
            Import counters: completed {unavailable(retained.importCounters.completed)}; failed{' '}
            {unavailable(retained.importCounters.failed)}. Counters do not establish membership or
            verification.
          </p>
          {retained.membership.nextCursor && (
            <button type="button" disabled={busy} onClick={() => void load(true)}>
              Load next results page
            </button>
          )}
          <button
            type="button"
            disabled={busy || !retained.complete || !downloadFits}
            onClick={() => save(download, `checklist-import-results-${retained.batchId}.json`)}
          >
            Download complete batch results (JSON)
          </button>
          {!downloadFits && (
            <p>
              The complete collection exceeds the 8 MiB download limit. All loaded products remain
              available; download individual target results or verification reports.
            </p>
          )}
          {retained.products.map((product) => {
            const v = product.verification,
              report = reports[product.sourceTargetId];
            return (
              <article key={product.sourceTargetId} aria-label={`Target ${product.sourceTargetId}`}>
                <h4>Target {product.sourceTargetId}</h4>
                <p>
                  Import outcome: {product.importStatus}. Mode: {product.mode}. Fingerprint:{' '}
                  {product.fingerprint}.
                </p>
                <p>
                  Catalog verification: {v.status}. Freshness: {product.freshness.status} (checked{' '}
                  {product.freshness.checkedAt}).{' '}
                  {v.status === 'VERIFIED_COMPLETE' && product.freshness.status !== 'CURRENT'
                    ? 'This historical pass is not currently verified.'
                    : ''}
                </p>
                <p>
                  Phase: {v.phase}; version {v.version}. Evidence coverage:{' '}
                  {product.evidence.coverage}.
                </p>
                <p>
                  Originally imported SHA-256: {product.evidence.inputSha256 ?? 'Unknown'}. Claimed
                  input match: {product.evidence.claimedInputMatch}.
                </p>
                <table>
                  <caption>Import counts and source changes</caption>
                  <thead>
                    <tr>
                      <th>Kind</th>
                      <th>Input</th>
                      <th>Observed</th>
                      <th>Adds</th>
                      <th>Updates</th>
                      <th>Unchanged</th>
                      <th>Source missing</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(['roster', 'parallelDefinitions', 'subsetDefinitions'] as const).map(
                      (kind) => (
                        <tr key={kind}>
                          <th>{kind}</th>
                          <td>{unavailable(product.inputCounts[kind])}</td>
                          <td>{unavailable(v.observedCounts[kind])}</td>
                          <td>{unavailable(product.diff[kind].adds)}</td>
                          <td>{unavailable(product.diff[kind].updates)}</td>
                          <td>{unavailable(product.diff[kind].unchanged)}</td>
                          <td>
                            {unavailable(product.diff[kind].sourceMissing)} (
                            {product.diff[kind].sourceMissingDisposition})
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
                <ul>
                  {Object.entries(v.checks).map(([key, value]) => (
                    <li key={key}>
                      {key}: {value}
                    </li>
                  ))}
                </ul>
                <p>
                  Mismatches: {v.mismatchTotal}
                  {v.samplesTruncated ? ' (samples truncated)' : ''}.
                </p>
                <ul>
                  {v.samples.map((sample, index) => (
                    <li key={index}>
                      {sample.code}: {sample.detail}{' '}
                      {sample.sourceItemId ? `Source item ${sample.sourceItemId}` : ''}{' '}
                      {sample.definitionId ?? ''} {sample.fieldPath ?? ''}
                    </li>
                  ))}
                </ul>
                {!v.sessionId && (
                  <button type="button" disabled={busy} onClick={() => void act(product, 'start')}>
                    Start verification for {product.sourceTargetId}
                  </button>
                )}
                {v.sessionId && (
                  <button type="button" disabled={busy} onClick={() => void act(product, 'start')}>
                    Start new verification for {product.sourceTargetId}
                  </button>
                )}
                {v.sessionId && (
                  <button type="button" disabled={busy} onClick={() => void act(product, 'status')}>
                    Check status for {product.sourceTargetId}
                  </button>
                )}
                {v.sessionId && !v.complete && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void act(product, 'advance')}
                  >
                    Advance verification for {product.sourceTargetId}
                  </button>
                )}
                {v.reportId && (
                  <button type="button" disabled={busy} onClick={() => void act(product, 'report')}>
                    Read verification report for {product.sourceTargetId}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() =>
                    save(
                      {
                        schemaVersion: 1,
                        reportType: 'CHECKLIST_IMPORT_PRODUCT_RESULT',
                        batchId: retained.batchId,
                        batchFingerprint: retained.batchFingerprint,
                        product,
                        verificationReport: report ?? null,
                        applyAllowed: false,
                      },
                      `checklist-import-result-${product.sourceTargetId}.json`,
                    )
                  }
                >
                  Download target {product.sourceTargetId} result (JSON)
                </button>
                {report && (
                  <>
                    <p>Report SHA-256: {report.report.digest}</p>
                    <button
                      type="button"
                      onClick={() =>
                        save(report, `checklist-verification-${product.sourceTargetId}.json`)
                      }
                    >
                      Download report for {product.sourceTargetId} (JSON)
                    </button>
                  </>
                )}
              </article>
            );
          })}
        </>
      )}
    </section>
  );
}
